import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Copy, Edit2, Inbox, Layers, Plus, Trash2, Webhook } from 'lucide-react';
import { crmMailboxesApi, crmPipelineApi } from '../../api/crm';
import { companiesApi } from '../../api/companies';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfirm } from '../ConfirmDialog';
import Modal from '../Modal';
import EmptyState from '../EmptyState';
import Field from '../crm/Field';
import type { CrmMailbox, CrmMailboxSettings, CrmPipelineStage } from '../../types';

const STAFF_ROLES = new Set(['owner', 'admin', 'technician']);

// ─────────────────────────────────────────────────────────────
// Company Settings → CRM: the mailboxes that feed the Inbox, the pipeline
// stages deals move through, and how to point a contact form at the
// lead API. Everything here is admin-only (the tab is behind the settings
// page's own gate).
// ─────────────────────────────────────────────────────────────

const lines = (list: string[]) => list.join('\n');
const fromLines = (text: string) => text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

interface MailboxDraft {
  address: string;
  name: string;
  isActive: boolean;
  fromName: string;
  fromAddress: string;
  signature: string;
  notifyEmails: string;
  defaultOwnerId: string;
  autoReply: boolean;
  autoReplyTemplate: string;
  threadWindowDays: string;
  spamScoreThreshold: string;
  spamAllowlist: string;
  blockedSenders: string;
}

function draftFrom(m: CrmMailbox | null): MailboxDraft {
  const s = m?.settings;
  return {
    address: m?.address ?? '',
    name: m?.name ?? '',
    isActive: m?.isActive ?? true,
    fromName: s?.fromName ?? '',
    fromAddress: s?.fromAddress ?? '',
    signature: s?.signature ?? '',
    notifyEmails: lines(s?.notifyEmails ?? []),
    defaultOwnerId: s?.defaultOwnerId ?? '',
    autoReply: s?.autoReply ?? false,
    autoReplyTemplate: s?.autoReplyTemplate ?? '',
    threadWindowDays: String(s?.threadWindowDays ?? 14),
    spamScoreThreshold: String(s?.spamScoreThreshold ?? 7),
    spamAllowlist: lines(s?.spamAllowlist ?? []),
    blockedSenders: lines(s?.blockedSenders ?? []),
  };
}

function settingsFrom(d: MailboxDraft): Partial<CrmMailboxSettings> {
  return {
    fromName: d.fromName,
    fromAddress: d.fromAddress,
    signature: d.signature,
    notifyEmails: fromLines(d.notifyEmails),
    defaultOwnerId: d.defaultOwnerId || null,
    autoReply: d.autoReply,
    autoReplyTemplate: d.autoReplyTemplate,
    threadWindowDays: Number(d.threadWindowDays),
    spamScoreThreshold: Number(d.spamScoreThreshold),
    spamAllowlist: fromLines(d.spamAllowlist),
    blockedSenders: fromLines(d.blockedSenders),
  };
}

function MailboxModal({ mailbox, staff, onClose, onSaved }: { mailbox: CrmMailbox | null; staff: any[]; onClose: () => void; onSaved: () => void }) {
  const [d, setD] = useState<MailboxDraft>(() => draftFrom(mailbox));
  const [error, setError] = useState('');
  const set = <K extends keyof MailboxDraft>(k: K, v: MailboxDraft[K]) => setD(x => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const body = { address: d.address, name: d.name || d.address.split('@')[0], isActive: d.isActive, settings: settingsFrom(d) };
      return mailbox ? crmMailboxesApi.update(mailbox.id, body) : crmMailboxesApi.create(body);
    },
    onSuccess: () => { toast.success(mailbox ? 'Mailbox saved' : 'Mailbox added'); onSaved(); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Could not save'),
  });

  return (
    <Modal open onClose={onClose} title={mailbox ? `Edit ${mailbox.name}` : 'Add a CRM mailbox'} width={680} busy={save.isPending}
      footer={<>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!d.address.trim() || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save mailbox'}</button>
      </>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Address" required><input className="input w-full" value={d.address} onChange={e => set('address', e.target.value)} placeholder="sales@yourcompany.com" /></Field>
          <Field label="Mailbox name"><input className="input w-full" value={d.name} onChange={e => set('name', e.target.value)} placeholder="Sales" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={d.isActive} onChange={e => set('isActive', e.target.checked)} /> Accepting mail
          <span className="text-xs text-muted">— turn off to stop new mail without losing the conversations</span>
        </label>

        <h4 className="section-title pt-2">Sending</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Sender name"><input className="input w-full" value={d.fromName} onChange={e => set('fromName', e.target.value)} placeholder={d.name || 'Sales'} /></Field>
          <Field label="Send from address"><input className="input w-full" value={d.fromAddress} onChange={e => set('fromAddress', e.target.value)} placeholder="Leave blank for the system address" /></Field>
        </div>
        <Field label="Signature"><textarea className="input w-full min-h-[80px]" value={d.signature} onChange={e => set('signature', e.target.value)} placeholder={'Chris\nCozziTech · 555-0100'} /></Field>
        <p className="text-xs text-muted">
          Sales mail is sent with its full text (unlike ticket notifications, which are content-free). Do not use a CRM mailbox for anything that could contain regulated data.
        </p>

        <h4 className="section-title pt-2">Who hears about new mail</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Notify" hint="One address per line."><textarea className="input w-full min-h-[70px]" value={d.notifyEmails} onChange={e => set('notifyEmails', e.target.value)} placeholder={'you@yourcompany.com'} /></Field>
          <Field label="Default owner">
            <select className="select w-full" value={d.defaultOwnerId} onChange={e => set('defaultOwnerId', e.target.value)}>
              <option value="">Nobody — triage from Unassigned</option>
              {staff.map((m: any) => { const uid = m.user?.id ?? m.userId; return <option key={uid} value={uid}>{m.user?.name || m.user?.email}</option>; })}
            </select>
          </Field>
        </div>

        <h4 className="section-title pt-2">Auto-reply</h4>
        <label className="flex items-center gap-2 text-sm text-fg" title="Send an automatic acknowledgement to people writing in for the first time. Never sent to replies.">
          <input type="checkbox" checked={d.autoReply} onChange={e => set('autoReply', e.target.checked)} /> Acknowledge new senders automatically
        </label>
        {d.autoReply && <Field label="Auto-reply text"><textarea className="input w-full min-h-[70px]" value={d.autoReplyTemplate} onChange={e => set('autoReplyTemplate', e.target.value)} placeholder="Thanks for getting in touch — we will get back to you within one business day." /></Field>}

        <h4 className="section-title pt-2">Threading and filtering</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Thread window (days)"><input className="input w-full" type="number" min="0" max="365" value={d.threadWindowDays} onChange={e => set('threadWindowDays', e.target.value)} /></Field>
          <Field label="Spam threshold"><input className="input w-full" type="number" min="0" max="100" step="0.5" value={d.spamScoreThreshold} onChange={e => set('spamScoreThreshold', e.target.value)} /></Field>
          <Field label="Always allow"><textarea className="input w-full min-h-[70px]" value={d.spamAllowlist} onChange={e => set('spamAllowlist', e.target.value)} placeholder={'partner.com\nsomeone@example.com'} /></Field>
          <Field label="Blocked senders"><textarea className="input w-full min-h-[70px]" value={d.blockedSenders} onChange={e => set('blockedSenders', e.target.value)} placeholder={'spammer.example'} /></Field>
        </div>
        {error && <p className="text-sm text-err">{error}</p>}
      </div>
    </Modal>
  );
}

function StageModal({ stage, onClose, onSaved }: { stage: CrmPipelineStage | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: stage?.name ?? '', color: stage?.color ?? '#6366f1',
    probability: String(stage?.probability ?? 10), description: stage?.description ?? '',
    outcome: (stage?.outcome ?? 'open') as 'open' | 'won' | 'lost',
  });
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(), color: form.color, probability: Number(form.probability),
        description: form.description || undefined, outcome: form.outcome,
      };
      return stage ? crmPipelineApi.update(stage.id, body) : crmPipelineApi.create(body);
    },
    onSuccess: () => { toast.success(stage ? 'Stage saved' : 'Stage added'); onSaved(); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Could not save'),
  });
  return (
    <Modal open onClose={onClose} title={stage ? 'Edit stage' : 'Add a stage'} busy={save.isPending}
      footer={<>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!form.name.trim() || save.isPending} onClick={() => save.mutate()}>Save</button>
      </>}
    >
      <div className="space-y-4">
        <Field label="Stage name" required><input className="input w-full" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Win chance %"><input className="input w-full" type="number" min="0" max="100" value={form.probability} onChange={e => setForm(f => ({ ...f, probability: e.target.value }))} /></Field>
          <Field label="Color"><input className="input w-full h-10" type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} /></Field>
        </div>
        <Field label="When a deal lands here" hint="Won and Lost close the deal automatically when you drag a card into this column.">
          <select className="select w-full" value={form.outcome} onChange={e => setForm(f => ({ ...f, outcome: e.target.value as 'open' | 'won' | 'lost' }))}>
            <option value="open">Still working it</option>
            <option value="won">Mark the deal won</option>
            <option value="lost">Mark the deal lost</option>
          </select>
        </Field>
        <Field label="Description"><input className="input w-full" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional — what has to be true to be here" /></Field>
        {error && <p className="text-sm text-err">{error}</p>}
      </div>
    </Modal>
  );
}

export default function CrmSettingsTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data: mailboxes, isLoading } = useQuery({ queryKey: ['crm-mailboxes', cid], queryFn: () => crmMailboxesApi.list(), enabled: !!cid });
  const { data: members } = useQuery({ queryKey: ['company-members', cid], queryFn: () => companiesApi.getMembers(cid), enabled: !!cid });
  const { data: stages } = useQuery({ queryKey: ['crm-pipeline', cid], queryFn: () => crmPipelineApi.list(), enabled: !!cid });
  const staff = useMemo(() => ((members as any[]) || []).filter(m => STAFF_ROLES.has(m.role) && m.isActive !== false), [members]);

  const [editing, setEditing] = useState<CrmMailbox | 'new' | null>(null);
  const [stageEditing, setStageEditing] = useState<CrmPipelineStage | 'new' | null>(null);

  const refreshMailboxes = () => { qc.invalidateQueries({ queryKey: ['crm-mailboxes'] }); setEditing(null); };
  const refreshStages = () => { qc.invalidateQueries({ predicate: q => String(q.queryKey[0]).startsWith('crm-') }); setStageEditing(null); };

  const deleteMailbox = useMutation({
    mutationFn: (id: string) => crmMailboxesApi.delete(id),
    onSuccess: () => { toast.success('Mailbox deleted'); refreshMailboxes(); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not delete'),
  });
  const toggleMailbox = useMutation({
    mutationFn: (m: CrmMailbox) => crmMailboxesApi.update(m.id, { isActive: !m.isActive }),
    onSuccess: refreshMailboxes,
  });
  const reorder = useMutation({
    mutationFn: (list: { id: string; order: number }[]) => crmPipelineApi.reorder(list),
    onSuccess: refreshStages,
  });
  const removeStage = useMutation({
    mutationFn: (id: string) => crmPipelineApi.delete(id),
    onSuccess: () => { toast.success('Stage removed'); refreshStages(); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not remove'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    if (!stages) return;
    const list = [...stages];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    reorder.mutate(list.map((s, i) => ({ id: s.id, order: i })));
  };

  const endpoint = `${window.location.origin}/api/v1/crm/leads`;
  const sample = JSON.stringify({
    email: 'jane@example.com', name: 'Jane Doe', phone: '555-0100', company: 'Example Inc',
    subject: 'Demo request', message: 'We would like to see ctAgency Suite.', formName: 'Contact us',
    pageUrl: 'https://www.cozzitech.com/contact', fields: { Interest: 'ctAgency Suite', Agency_size: '25-50' },
  }, null, 2);
  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error('Copy failed'); }
  };

  useEffect(() => { /* keep the tab mounted state simple */ }, []);

  return (
    <div className="space-y-6">
      {/* ── Mailboxes ── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="section-title flex items-center gap-2"><Inbox className="w-4 h-4" /> Inbox mailboxes</h2>
          <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={() => setEditing('new')}><Plus className="w-4 h-4" /> Add mailbox</button>
        </div>
        <p className="text-sm text-muted mb-4">
          Addresses like <b>sales@</b> or <b>info@</b> whose mail should land in <Link to="/crm/inbox" className="text-primary hover:underline">CRM → Inbox</Link> instead of becoming tickets.
          Point the address at this server exactly like your ticket address (see the <b>Email</b> tab). The sender is matched to a contact, replies go out from here, and answers thread back.
        </p>
        {isLoading ? (
          <div className="h-16 bg-surface rounded animate-pulse" />
        ) : !mailboxes?.length ? (
          <EmptyState icon={<Inbox className="w-6 h-6" />}>No mailboxes yet. Click <strong>Add mailbox</strong> and enter the address customers write to.</EmptyState>
        ) : (
          <div className="space-y-2">
            {mailboxes.map(m => (
              <div key={m.id} className="flex items-center gap-3 py-2 border-b border-line-light last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-fg">{m.name} <span className="font-mono text-xs text-muted ml-1">{m.address}</span>{!m.isActive && <span className="badge ml-2 text-warn">Off</span>}</p>
                  <p className="text-xs text-muted">
                    {m.conversationCount} conversation{m.conversationCount === 1 ? '' : 's'}
                    {m.settings.notifyEmails.length > 0 && ` · notifies ${m.settings.notifyEmails.length}`}
                    {m.settings.autoReply && ' · auto-reply on'}
                    {m.settings.fromAddress && ` · sends from ${m.settings.fromAddress}`}
                  </p>
                </div>
                <button type="button" className="btn btn-secondary text-xs" onClick={() => toggleMailbox.mutate(m)} title={m.isActive ? 'Stop accepting mail' : 'Accept mail again'}>{m.isActive ? 'Turn off' : 'Turn on'}</button>
                <button type="button" className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-row-odd" onClick={() => setEditing(m)} title="Edit"><Edit2 className="w-4 h-4" /></button>
                <button type="button" className="p-1.5 rounded-md text-muted hover:text-err hover:bg-row-odd" title="Delete"
                  onClick={async () => { if (await confirm({ title: 'Delete mailbox', message: `Delete ${m.address}? Only possible while it has no conversations.`, confirmLabel: 'Delete', danger: true })) deleteMailbox.mutate(m.id); }}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Pipeline stages ── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="section-title flex items-center gap-2"><Layers className="w-4 h-4" /> Pipeline stages</h2>
          <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={() => setStageEditing('new')}><Plus className="w-4 h-4" /> Add stage</button>
        </div>
        <p className="text-sm text-muted mb-4">The columns on the Deals board, in order. The win chance feeds forecasting. Removing a stage hides it; deals already in it keep it.</p>
        <div className="space-y-1">
          {(stages ?? []).map((s, i) => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-line-light last:border-0">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-sm text-fg flex-1">{s.name}{s.isDefault && <span className="text-xs text-muted ml-2">(new deals start here)</span>}</span>
              {s.outcome && s.outcome !== 'open' && (
                <span className="badge" style={{ color: s.outcome === 'won' ? 'var(--scpa-status-ok)' : 'var(--scpa-status-error)' }}>
                  {s.outcome === 'won' ? 'Closes as won' : 'Closes as lost'}
                </span>
              )}
              <span className="text-xs text-muted w-16 text-right">{s.probability}% win</span>
              <button type="button" className="p-1 text-muted hover:text-fg disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} title="Move up"><ArrowUp className="w-4 h-4" /></button>
              <button type="button" className="p-1 text-muted hover:text-fg disabled:opacity-30" disabled={i === (stages?.length ?? 0) - 1} onClick={() => move(i, 1)} title="Move down"><ArrowDown className="w-4 h-4" /></button>
              <button type="button" className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-row-odd" onClick={() => setStageEditing(s)} title="Edit"><Edit2 className="w-4 h-4" /></button>
              <button type="button" className="p-1.5 rounded-md text-muted hover:text-err hover:bg-row-odd" title="Remove"
                onClick={async () => { if (await confirm({ title: 'Remove stage', message: `Remove "${s.name}" from the board?`, confirmLabel: 'Remove', danger: true })) removeStage.mutate(s.id); }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Lead API ── */}
      <div className="card p-6">
        <h2 className="section-title flex items-center gap-2 mb-2"><Webhook className="w-4 h-4" /> Contact-form leads</h2>
        <p className="text-sm text-muted mb-4">
          Let a website form post straight into the Inbox. Each submission becomes a conversation with source <b>Contact form</b>, the person becomes a contact (or matches one), and the mailbox notify list hears about it.
          Forms that only send an email notification can simply email your mailbox address instead.
        </p>
        <ol className="text-sm text-fg space-y-2 list-decimal pl-5">
          <li>Create an API key on the <b>Integrations</b> tab with the <b>Post CRM leads</b> scope.</li>
          <li>Have the form host send a JSON <code className="font-mono text-xs">POST</code> to
            <code className="font-mono text-xs bg-surface px-1.5 py-0.5 rounded ml-1">{endpoint}</code>
            <button type="button" className="ml-1 p-1 text-muted hover:text-primary align-middle" onClick={() => copy(endpoint, 'Endpoint')} title="Copy"><Copy className="w-3.5 h-3.5 inline" /></button>
            with header <code className="font-mono text-xs">Authorization: Bearer &lt;key&gt;</code>.
          </li>
          <li>Only <code className="font-mono text-xs">email</code> is required. Add <code className="font-mono text-xs">mailbox</code> (an address from above) to choose which mailbox it files under; otherwise the first active one is used. Send an <code className="font-mono text-xs">Idempotency-Key</code> header so retries don't double up.</li>
        </ol>
        <div className="relative mt-4">
          <pre className="text-xs font-mono bg-surface border border-line rounded-lg p-3 overflow-x-auto">{sample}</pre>
          <button type="button" className="absolute top-2 right-2 btn btn-secondary text-xs inline-flex items-center gap-1" onClick={() => copy(sample, 'Sample')}><Copy className="w-3 h-3" /> Copy</button>
        </div>
      </div>

      {editing && <MailboxModal mailbox={editing === 'new' ? null : editing} staff={staff} onClose={() => setEditing(null)} onSaved={refreshMailboxes} />}
      {stageEditing && <StageModal stage={stageEditing === 'new' ? null : stageEditing} onClose={() => setStageEditing(null)} onSaved={refreshStages} />}
    </div>
  );
}
